import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Tab,
  Tabs,
} from 'react-bootstrap';
import { AlertTriangle, Info, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import PageLayout from '../../layouts/PageLayout';
import { taskDurationAPI } from '../../services';
import { useBranch } from '../../hooks';

/**
 * #123903 — "Phân tích thời gian Task": xem kết quả khảo sát #142431 ở MỘT chỗ, và để máy tự chỉ
 * ra các lượt khai đáng ngờ kèm lý do.
 *
 * VÌ SAO ĐẶT Ở ĐÂY (nhóm "Nhân viên và Ca làm", ngay dưới "Cấu hình Task"): con số đem ra so là
 * "Thời gian ước tính" cấu hình trong `/tasks`, nên người xem thấy lệch là bấm sang sửa chuẩn ngay
 * cạnh — tách sang nhóm Báo cáo sẽ bắt họ đi vòng. Cố ý KHÔNG nhồi thêm vào `/tasks`: màn đó đã 16
 * cột phải cuộn ngang (#160259), thêm nữa là hỏng đúng thứ vừa sửa.
 *
 * BA ĐIỀU ĐỪNG ĐỔI:
 * 1. Màn này CHỈ CẢNH BÁO — không có nút phạt, không gọi API điểm. Sếp chưa hề nói phạt ở #123903.
 * 2. Công thức + ngưỡng đọc từ `threshold_explanations` server trả về, KHÔNG chép cứng ở đây; chép
 *    lại là bảo đảm có ngày màn hình nói một đằng máy tính một nẻo.
 * 3. Chờ BranchContext nạp xong (`loading`) rồi mới gọi — gọi sớm hơn là ra "Không thể tải" dù API
 *    trả 200. Nhưng KHÔNG chặn theo `selectedBranch?.id`: thanh chọn chi nhánh có lựa chọn hợp lệ
 *    "-- Tất cả chi nhánh --", và ADMIN xem toàn hệ thống chính là đường dùng chính của sếp. Bỏ
 *    trống `branch_id` thì server tự xử đúng vai: ADMIN thấy tất cả, MANAGER vẫn bị kẹp về chi
 *    nhánh đang làm việc (không lách được bằng cách bỏ tham số).
 */

const LEVEL_META = {
  HIGH: { bg: 'danger', text: 'Rất đáng ngờ' },
  WATCH: { bg: 'warning', text: 'Cần để ý' },
  NONE: { bg: 'light', text: 'Bình thường' },
};

const fmt = (n) =>
  n === null || n === undefined ? '—' : String(Math.round(n * 10) / 10).replace('.', ',');

const fmtGap = (n) => (n === null || n === undefined ? '—' : `${n > 0 ? '+' : ''}${fmt(n)}`);

/**
 * Mẫu task là RIÊNG từng chi nhánh và hay TRÙNG TÊN nhau (dữ liệu thật có 4 mẫu "CV trước khi out
 * ca"). Thiếu dòng chi nhánh này thì bảng của ADMIN là mấy dòng giống hệt nhau, không đọc được.
 */
function TaskName({ name, branchName }) {
  return (
    <>
      {name || '(không tên)'}
      {branchName && <div className="small text-muted">{branchName}</div>}
    </>
  );
}

export default function TaskDurationAnalysisPage() {
  const { selectedBranch, loading: branchLoading } = useBranch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [onlyFlagged, setOnlyFlagged] = useState(true);
  const [showFormula, setShowFormula] = useState(false);

  const loadData = useCallback(async () => {
    if (branchLoading) return;
    try {
      setLoading(true);
      setError(null);
      const res = await taskDurationAPI.getAnomalies({
        // Không chọn chi nhánh nào = "tất cả" — cứ bỏ trống, server kẹp theo vai người gọi.
        ...(selectedBranch?.id ? { branch_id: selectedBranch.id } : {}),
        only_flagged: onlyFlagged,
      });
      setData(res?.data ?? res ?? null);
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.message || 'Không tải được dữ liệu phân tích thời gian task';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedBranch?.id, onlyFlagged, branchLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = data?.summary ?? null;
  const tasks = useMemo(() => data?.tasks ?? [], [data]);
  const reports = useMemo(() => data?.reports ?? [], [data]);

  // Chưa có mẫu khảo sát nào ⇒ trạng thái rỗng tử tế, không phải bảng trắng khó hiểu.
  const noSurveyData = !loading && !error && summary && summary.total_reports === 0;

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header my-2 d-flex align-items-center gap-2 flex-wrap">
          <h1 className="mb-0">Phân tích thời gian Task</h1>
          <Badge bg="primary">{selectedBranch?.name || 'Tất cả chi nhánh'}</Badge>
          <div className="ms-auto d-flex align-items-center gap-2">
            <Form.Check
              type="switch"
              id="tda-only-flagged"
              label="Chỉ xem lượt đáng ngờ"
              checked={onlyFlagged}
              onChange={(e) => setOnlyFlagged(e.target.checked)}
            />
            <Button variant="outline-secondary" size="sm" onClick={loadData} disabled={loading}>
              {loading ? <Spinner size="sm" animation="border" /> : <RefreshCw size={16} />}
              <span className="ms-1">Làm mới</span>
            </Button>
          </div>
        </div>

        <Alert variant="secondary" className="py-2">
          <AlertTriangle size={16} className="me-1" />
          Màn này <strong>chỉ cảnh báo</strong> — máy không tự trừ điểm hay phạt ai. Con số là do
          nhân viên <strong>tự khai</strong> sau khi bấm hoàn thành, nên hãy hỏi lại người liên quan
          trước khi kết luận.
        </Alert>

        {error ? (
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        ) : loading && !data ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {summary && (
              <Row className="g-2 mb-3">
                <SummaryCard label="Lượt khai đã nhận" value={summary.total_reports} />
                <SummaryCard
                  label="Bị đánh dấu đáng ngờ"
                  value={summary.flagged_reports}
                  variant={summary.flagged_reports > 0 ? 'warning' : undefined}
                />
                <SummaryCard
                  label="Rất đáng ngờ"
                  value={summary.high_reports}
                  variant={summary.high_reports > 0 ? 'danger' : undefined}
                />
                <SummaryCard label="Loại task có dữ liệu" value={summary.task_count} />
                <SummaryCard label="Nhân viên bị đánh dấu" value={summary.flagged_employee_count} />
              </Row>
            )}

            <Card className="mb-3">
              <Card.Body className="py-2">
                <Button
                  variant="link"
                  className="p-0 text-decoration-none"
                  onClick={() => setShowFormula((v) => !v)}
                >
                  <Info size={16} className="me-1" />
                  Máy đang dựa vào đâu để kết luận? {showFormula ? '▲' : '▼'}
                </Button>
                {showFormula && (
                  <div className="mt-2">
                    <ul className="mb-2 ps-3">
                      {(data?.threshold_explanations ?? []).map((t) => (
                        <li key={t.code} className="mb-1">
                          <strong>{t.title}:</strong> {t.formula}
                        </li>
                      ))}
                    </ul>
                    <p className="text-muted small mb-1">
                      Một lượt khai bật <strong>từ 2 dấu hiệu trở lên</strong>, hoặc mâu thuẫn với
                      mốc thời gian hệ thống ghi được, thì xếp mức <em>Rất đáng ngờ</em>; đúng 1 dấu
                      hiệu mềm thì xếp <em>Cần để ý</em>.
                    </p>
                    <p className="text-muted small mb-0">
                      Đổi ngưỡng không cần sửa mã: sửa cấu hình khoá{' '}
                      <code>{data?.config_key}</code> (kiểu JSON) trong màn Cấu hình hệ thống — đặt
                      theo chi nhánh cũng được. Ngưỡng đang chạy:{' '}
                      <code>{JSON.stringify(data?.thresholds ?? {})}</code>
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {noSurveyData ? (
              <Card>
                <Card.Body className="text-center text-muted py-5">
                  {selectedBranch?.name ? `Chi nhánh ${selectedBranch.name}` : 'Hệ thống'} chưa có
                  lượt khảo sát thời gian nào. Bảng sẽ tự có dữ liệu khi nhân viên bấm hoàn thành
                  task và chọn số phút.
                </Card.Body>
              </Card>
            ) : (
              <Tabs defaultActiveKey="tasks" className="mb-3">
                <Tab eventKey="tasks" title={`Theo loại task (${tasks.length})`}>
                  <TaskRollupTable rows={tasks} />
                </Tab>
                <Tab
                  eventKey="reports"
                  title={`Từng lượt khai (${data?.matched ?? reports.length})`}
                >
                  <ReportTable rows={reports} onlyFlagged={onlyFlagged} />
                </Tab>
              </Tabs>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
}

function SummaryCard({ label, value, variant }) {
  return (
    <Col xs={6} md={4} lg>
      <Card className={variant ? `border-${variant}` : undefined}>
        <Card.Body className="py-2">
          <div className="text-muted small">{label}</div>
          <div className={`fs-4 fw-bold ${variant ? `text-${variant}` : ''}`}>{value ?? 0}</div>
        </Card.Body>
      </Card>
    </Col>
  );
}

/** Bảng tổng: một dòng một loại task — chỗ "nhìn một cái là hiểu" của đề bài. */
function TaskRollupTable({ rows }) {
  if (!rows.length) {
    return <p className="text-muted text-center py-4 mb-0">Chưa có loại task nào có dữ liệu.</p>;
  }
  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle">
        <thead>
          <tr>
            <th>Loại task</th>
            <th className="text-end">Chuẩn đang đặt</th>
            <th className="text-end">NV khai trung bình</th>
            <th className="text-end">Lệch</th>
            <th className="text-end">Số lượt khai</th>
            <th className="text-end">Số người khai</th>
            <th className="text-end">Lượt đáng ngờ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.task_id}>
              <td>
                <TaskName name={r.task_name} branchName={r.task_branch_name} />
              </td>
              <td className="text-end">
                {r.standard_minutes == null ? (
                  <span className="text-muted">chưa đặt</span>
                ) : (
                  `${r.standard_minutes} phút`
                )}
              </td>
              <td className="text-end">
                {r.average_declared_minutes == null ? (
                  <span className="text-muted">
                    chưa đủ mẫu ({r.total_reports})
                  </span>
                ) : (
                  `${fmt(r.average_declared_minutes)} phút`
                )}
              </td>
              <td
                className={`text-end ${
                  r.gap_minutes != null && r.gap_minutes > 0 ? 'text-danger fw-semibold' : ''
                }`}
              >
                {fmtGap(r.gap_minutes)}
              </td>
              <td className="text-end">{r.total_reports}</td>
              <td className="text-end">{r.employee_count}</td>
              <td className="text-end">
                {r.flagged_reports > 0 ? (
                  <Badge bg={r.high_reports > 0 ? 'danger' : 'warning'}>
                    {r.flagged_reports}
                    {r.high_reports > 0 ? ` (${r.high_reports} nặng)` : ''}
                  </Badge>
                ) : (
                  <span className="text-muted">0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Bảng chi tiết từng lượt khai, lệch nhiều nhất lên đầu, mỗi cờ kèm lý do bằng chữ. */
function ReportTable({ rows, onlyFlagged }) {
  if (!rows.length) {
    return (
      <p className="text-muted text-center py-4 mb-0">
        {onlyFlagged
          ? 'Không có lượt khai nào đáng ngờ theo ngưỡng hiện tại. 👍'
          : 'Chưa có lượt khai nào.'}
      </p>
    );
  }
  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle">
        <thead>
          <tr>
            <th>Mức</th>
            <th>Loại task</th>
            <th>Nhân viên</th>
            <th>Ca</th>
            <th className="text-end">Khai</th>
            <th className="text-end">Chuẩn</th>
            <th className="text-end">Đồng nghiệp</th>
            <th className="text-end">Thời gian thật</th>
            <th>Vì sao bị đánh dấu</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const meta = LEVEL_META[r.level] ?? LEVEL_META.NONE;
            return (
              <tr key={r.id}>
                <td>
                  <Badge bg={meta.bg} text={meta.bg === 'light' ? 'dark' : undefined}>
                    {meta.text}
                  </Badge>
                </td>
                <td>
                  <TaskName name={r.task_name} branchName={r.task_branch_name} />
                </td>
                <td>{r.employee_name || '—'}</td>
                <td className="small text-muted">{r.shift_name || '—'}</td>
                <td className="text-end fw-semibold">{r.declared_minutes} phút</td>
                <td className="text-end">
                  {r.standard_minutes == null ? '—' : `${r.standard_minutes} phút`}
                </td>
                <td className="text-end">
                  {r.peer_median_minutes == null ? (
                    <span
                      className="text-muted"
                      title={`Mới có ${r.peer_sample_count} người khác khai — chưa đủ để so`}
                    >
                      chưa đủ
                    </span>
                  ) : (
                    <span
                      title={
                        r.peer_scope === 'SAME_BRANCH'
                          ? `Mức giữa của ${r.peer_sample_count} người cùng chi nhánh`
                          : `Chi nhánh chưa đủ người, lấy mức giữa của ${r.peer_sample_count} người toàn hệ thống`
                      }
                    >
                      {fmt(r.peer_median_minutes)} phút
                      {r.peer_scope === 'ALL_BRANCHES' && <sup className="text-muted"> *</sup>}
                    </span>
                  )}
                </td>
                <td className="text-end">
                  {r.elapsed_minutes == null ? '—' : `${fmt(r.elapsed_minutes)} phút`}
                </td>
                <td style={{ minWidth: 320 }}>
                  {r.signals.length === 0 ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <ul className="mb-0 ps-3 small">
                      {r.signals.map((s) => (
                        <li key={s.code}>{s.reason}</li>
                      ))}
                    </ul>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.some((r) => r.peer_median_minutes != null && r.peer_scope === 'ALL_BRANCHES') && (
        <p className="text-muted small mb-0">
          <sup>*</sup> chi nhánh chưa đủ người làm cùng task nên mức giữa lấy từ toàn hệ thống.
        </p>
      )}
    </div>
  );
}
